import { makeObservable, observable } from 'mobx';
import { ChatMessage } from './components/ChatMessage';
import Attachments from './components/Attachments';
import History from './components/History';
import { observer } from 'mobx-react-lite';
import TTT from './components/TextArea';
import { Box, getVariant } from '@apia/theme';
import { AudioRecorder } from '@apia/util';
import { CameraController } from './CameraController';
import { getBase64FromBlob, getBase64FromFile } from './util';
import {
  IAttachment,
  MessageCallbackProps,
  MessageSubmitCallback,
  TChatControllerState,
} from './types';

export class ChatController<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  currentIndex = 0;
  state: TChatControllerState & T = {
    isLoading: false,
    canRecord: true,
    canAttach: true,
    maxAttachmentsSize: 100,
    messages: [],
    currentMessage: new ChatMessage('', 'user'),
  } as unknown as TChatControllerState & T;
  private internalAudioRecorder = new AudioRecorder();
  private internalCameraRecorder = new CameraController();

  private maxId = 0;

  audioRecorder = {
    start: () => {
      this.state.isLoading = true;
      this.internalAudioRecorder
        .start()
        .then(async (blob: Blob) => {
          this.state.currentMessage.attachments.push({
            id: String(this.maxId++),
            name: `userRecord_${this.audioRecorder.record++}.wav`,
            value: await getBase64FromBlob(blob),
          });
          this.handleSubmit();
        })
        .finally(() => {
          this.state.isLoading = false;
        });
    },
    stop: async () => {
      this.internalAudioRecorder.stop();
    },
    record: 0,
    state: this.internalAudioRecorder.state,
  };

  constructor(
    props: Partial<TChatControllerState & T>,
    private onMessageSubmit: MessageSubmitCallback,
  ) {
    this.state = Object.assign({}, this.state, props);
    makeObservable(this, { state: observable });
  }

  removeMessage(idx: number) {
    this.state.messages = this.state.messages.filter((_, i) => i !== idx);
  }

  addMessage(message: ChatMessage) {
    this.state.messages.push(message);
    this.currentIndex = this.state.messages.length;
  }

  clearHistory() {
    this.state.messages = [];
  }

  clearMessage() {
    this.state.currentMessage = new ChatMessage('', 'user');
  }

  public removeAttachment(attachment: IAttachment) {
    this.state.currentMessage.removeAttachment(attachment);
  }

  private handleSubmit = async () => {
    if (
      this.state.currentMessage.message ||
      this.state.currentMessage.attachments.length > 0
    ) {
      this.state.isLoading = true;
      await this.onMessageSubmit({
        message: this.state.currentMessage,
        ...this.getAdditionalProps(),
      });
      this.state.isLoading = false;
      this.state.currentMessage = new ChatMessage('', 'user');
    }
  };

  private isForHistory(message?: ChatMessage) {
    return message && message.messageType === 'user';
  }

  private canGoNext() {
    let condition = false;
    const slice = this.state.messages.slice(
      this.currentIndex,
      this.state.messages.length,
    );
    const n = slice.length;
    if (this.currentIndex + 1 > n) {
      return condition;
    }
    for (let i = 0; i < n && !condition; i++) {
      const current = slice[i];
      condition = current.messageType === 'user';
    }
    return condition;
  }
  private canGoPrev() {
    let condition = false;
    if (this.currentIndex - 1 === -1) {
      return condition;
    }
    for (
      let i = this.state.messages.slice(0, this.currentIndex).length - 1;
      i >= 0 && !condition;
      i--
    ) {
      const current = this.state.messages[i];
      condition = current.messageType === 'user';
    }
    return condition;
  }
  public nextMessage() {
    let newCurrentMessage = undefined;
    let i = this.currentIndex;
    const n = this.state.messages.length;
    if (n === 0 || n - 1 === i) {
      return;
    }
    while (!newCurrentMessage && i < n - 1) {
      newCurrentMessage = this.state.messages[i + 1];
      i++;
      if (this.isForHistory(newCurrentMessage)) {
        this.state.currentMessage = newCurrentMessage!.clone();
        break;
      } else {
        newCurrentMessage = undefined;
      }
      if (i === this.currentIndex) {
        break;
      }
    }
    this.currentIndex = i;
  }

  public prevMessage() {
    let newCurrentMessage = undefined;
    let i = this.currentIndex;
    const n = this.state.messages.length;
    if (n === 0 || i === 0) {
      return;
    }
    while (!newCurrentMessage && i > 0) {
      newCurrentMessage = this.state.messages[i - 1];
      i--;
      if (this.isForHistory(newCurrentMessage)) {
        this.state.currentMessage = newCurrentMessage!.clone();
        break;
      } else {
        newCurrentMessage = undefined;
      }
      if (i === this.currentIndex) {
        break;
      }
    }
    this.currentIndex = i;
  }

  protected getAdditionalProps(): Partial<MessageCallbackProps> {
    return {};
  }

  History = observer(() => <History messages={this.state.messages}></History>);

  Attachments = observer(() => (
    <Attachments
      attachments={this.state.currentMessage.attachments}
      onRemove={(a) => {
        this.removeAttachment(a);
      }}
      onAddDescription={(a, ev) =>
        this.state.currentMessage.addAttachmentDescription(a, ev)
      }
    ></Attachments>
  ));

  TextArea = observer(() => (
    <TTT
      value={this.state.currentMessage.message as string}
      isLoading={this.state.isLoading}
      isRecording={this.internalAudioRecorder.state.recording}
      disablePrevButton={!this.canGoPrev()}
      disableNextButton={!this.canGoNext()}
      onChange={(ev) => {
        this.state.currentMessage.message = ev;
      }}
      onSubmit={() => this.handleSubmit()}
      onClearHistory={() => {
        this.clearHistory();
      }}
      onAttach={async (ev) => {
        this.state.currentMessage.attachments.push({
          ...ev,
          id: String(this.maxId++),
        });
      }}
      onRecord={() => {
        !this.internalAudioRecorder.state.recording
          ? this.audioRecorder.start()
          : this.audioRecorder.stop();
      }}
      onVideoRecord={async () => {
        !this.internalCameraRecorder.state.isOpen &&
          (await this.internalCameraRecorder.openCamera());
        await new Promise<void>((resolve, _) => {
          const checkCapture = async () => {
            if (!this.internalCameraRecorder.state.isOpen) {
              const capture = this.internalCameraRecorder.pop();
              if (capture) {
                this.state.currentMessage.attachments.push({
                  id: String(this.maxId++),
                  name: capture.name,
                  value: await getBase64FromFile(capture),
                });
                return resolve();
              }
            } else {
              requestAnimationFrame(checkCapture);
            }
          };

          checkCapture();
        });
      }}
      onPrev={() => {
        this.prevMessage();
      }}
      onNext={() => {
        this.nextMessage();
      }}
      attachments={[]}
    />
  ));

  Renderer = () => (
    <Box
      {...getVariant('layout.common.newChat.controller')}
      className="chat_controller"
    >
      <this.History />
      <this.Attachments />
      <this.TextArea />
    </Box>
  );
}
/*
const TTT = observer(
  ({
    isLoading,
    onSubmit,
    onChange,
    onClear,
    value,
  }: {
    isLoading: boolean;
    onClear: () => unknown;
    onSubmit: () => unknown;
    onChange: (v: string) => unknown;
    onAttach: (v: IAttachment) => unknown;
    value: string;
  }) => {
    return (
      <form
        onSubmit={(ev) => {
          ev.preventDefault();
          onSubmit();
        }}
      >
        <textarea
          value={value}
          onChange={(ev) => {
            onChange(ev.target.value);
          }}
          disabled={isLoading}
          name="r18923rasdjfh"
        ></textarea>
        <button type="submit">Enviar</button>
        <button
          type="button"
          onClick={() => {
            onClear();
          }}
        >
          Clear
        </button>
      </form>
    );
  },
);
*/
